import React, { useState, useEffect } from "react";
import { Button, Tooltip, Badge, Popover, Modal, Dropdown, Menu } from "antd";
import { DownloadOutlined, CalendarOutlined, LeftOutlined, RightOutlined, CaretDownOutlined, ExclamationCircleOutlined, CloseOutlined, CheckOutlined, CheckCircleOutlined } from "@ant-design/icons";
import IntlMessages from "util/IntlMessages";
import AdminNotifications from "../AdminNotifications";
import "./style.css";

const { confirm } = Modal;

const ReportActionButtons = ({
  // Quarter dropdown props
  quarterDropdownCurrent,
  onQuarterChange,
  quarterDropdown = [],

  // Action button props
  onSave,
  onApprove,
  onReject,
  onSubmit,
  onDownload,

  // Permission props
  isUserAdmin = false,
  isUserStaff = false,
  isHistoricQuarter = false,

  // Report status
  reportStatus = "",

  // Notification props
  messageCount = 0,
  messages = [],
  onNotificationClick,

  // Disable states
  disableSave = false,
  disableApprove = false,
  disableReject = false,
  disableSubmit = false,

  // Show/hide specific buttons
  showSave = true,
  showApprove = true,
  showReject = true,
  showSubmit = true,
  showDownload = true,
  showQuarterDropdown = true,
  showMessages = true,

  // Skip confirmation props (optional)
  skipApproveConfirm = false,
  skipRejectConfirm = false,
  skipSubmitConfirm = false,

  // Custom styles
  className = ""
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [yearDropdownVisible, setYearDropdownVisible] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);

  // Generate available years (current year and 5 years before and after)
  useEffect(() => {
      const currentYearNum = new Date().getFullYear();
      const years = [];
      for (let i = currentYearNum - 10; i <= currentYearNum; i++) {
        years.push(i);
      }
      setAvailableYears(years);
  }, []);

  // Quarter data mapping
  const quarterMap = [
    { id: 1, label: "Quarter 1", range: "Jan to Mar", shortRange: "Jan-Mar" },
    { id: 2, label: "Quarter 2", range: "Apr to Jun", shortRange: "Apr-Jun" },
    { id: 3, label: "Quarter 3", range: "Jul to Sep", shortRange: "Jul-Sep" },
    { id: 4, label: "Quarter 4", range: "Oct to Dec", shortRange: "Oct-Dec" }
  ];

  const changeQuarter = (quarter) => {
    if (onQuarterChange) {
      onQuarterChange(quarter);
    }
    setDropdownVisible(false);
  };

  // Format quarter display
  const formatQuarterDisplay = (quarter) => {
    if (quarter === "Loading...") return "Loading...";

    // If it's already in the format we want, return it
    if (quarter && quarter.includes("to")) return quarter;

    // Try to parse the quarter format
    if (quarter && quarter.includes("Q")) {
      const match = quarter.match(/Q(\d+)\s+(\d{4})/);
      if (match) {
        const qNum = parseInt(match[1]);
        const year = match[2];
        const qData = quarterMap.find(q => q.id === qNum);
        if (qData) {
          return `${qData.range} ${year}`;
        }
      }
    }

    return quarter || "Jan to March 2025";
  };

  // Confirmation modals
  const showApproveConfirm = () => {
    confirm({
      title: 'Are you sure you want to approve this report?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, Approve',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk() {
        if (onApprove) onApprove();
      },
    });
  };

  const showRejectConfirm = () => {
    confirm({
      title: 'Are you sure you want to reject this report?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, Reject',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        if (onReject) onReject();
      },
    });
  };

  const showSubmitConfirm = () => {
    confirm({
      title: 'Are you sure you want to submit this report?',
      icon: <CheckCircleOutlined />,
      content: 'This will submit the report for admin review.',
      okText: 'Yes, Submit',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk() {
        if (onSubmit) onSubmit();
      },
    });
  };

  // Create the dropdown menu
  const menu = (
    <Menu className="quarter-dropdown-menu">
      <Menu.Item key="header" style={{ padding: 0, cursor: 'default' }}>
        <div className="quarter-dropdown-header">
          <div className="year-selector">
            <LeftOutlined
              className="year-nav-button"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentYear(prev => prev - 1);
              }}
            />
            <Dropdown
              menu={{ items: availableYears.map(year => ({
                key: year,
                label: year.toString(),
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  setCurrentYear(year);
                  setYearDropdownVisible(false);
                }
              })) }}
              trigger={['click']}
              open={yearDropdownVisible}
              overlayClassName="custom-year-dropdown"
              onOpenChange={(visible) => {
                setYearDropdownVisible(visible);
              }}
            >
              <div
                className="year-dropdown"
                onClick={(e) => {
                  e.stopPropagation();
                  setYearDropdownVisible(!yearDropdownVisible);
                }}
              >
                <span className="year-text">{currentYear}</span>
                <CaretDownOutlined className="year-caret" />
              </div>
            </Dropdown>
            <RightOutlined
              className="year-nav-button"
              onClick={(e) => {
                e.stopPropagation();
                setCurrentYear(prev => prev + 1);
              }}
            />
          </div>
        </div>
      </Menu.Item>
      {quarterMap.map((quarter) => (
        <Menu.Item
          key={quarter.id}
          className={`quarter-item ${quarterDropdownCurrent && quarterDropdownCurrent.includes(quarter.shortRange) ? 'selected' : ''}`}
          onClick={() => changeQuarter(`${quarter.range} ${currentYear}`)}
        >
          <div style={{ display: 'flex' }}>
            <div className="quarter-number">{quarter.label}</div>
            <div className="quarter-date-range">{quarter.range}</div>
          </div>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <div className={`report-action-buttons ${className}`}>
      <div className="gx-flex-row gx-align-items-center">

        {/* Action Buttons */}
        <div className="action-buttons-group">
          {showSave && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={onSave}
                disabled={disableSave}
                className="mb-0"
                id="report-save"
              >
                Save
              </Button>
          )}

          {showApprove && (
            <Button
              className="gx-btn-success mb-0"
              icon={<CheckOutlined />}
              onClick={skipApproveConfirm ? onApprove : showApproveConfirm}
              disabled={disableApprove}
              id="report-approve"
            >
              Approve
            </Button>
          )}

          {showReject && (
            <Button
              className="gx-btn-danger mb-0"
              icon={<CloseOutlined />}
              onClick={skipRejectConfirm ? onReject : showRejectConfirm}
              disabled={disableReject}
              id="report-reject"
            >
              Reject
            </Button>
          )}

          {showSubmit && (
            <Button
              className="gx-btn-success mb-0"
              icon={<CheckOutlined />}
              onClick={skipSubmitConfirm ? onSubmit : showSubmitConfirm}
              disabled={disableSubmit}
              id="report-submit"
            >
              Submit Report
            </Button>
          )}
        </div>

        {/* Quarter Dropdown */}
        {showQuarterDropdown && (
          <div className="quarter-dropdown-container" style={{ position: 'relative' }}>
            <Dropdown
              open={dropdownVisible}
              onOpenChange={setDropdownVisible}
              trigger={['click']}
              menu={{ items: [] }}
              dropdownRender={() => menu}
              placement="bottomLeft"
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              overlayStyle={{
                zIndex: 1050,
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px'
              }}
              destroyPopupOnHide={true}
            >
              <div className="quarter-select-button" onClick={() => setDropdownVisible(!dropdownVisible)}>
                <CalendarOutlined className="calendar-icon" />
                <span className="quarter-text">
                  {quarterDropdownCurrent === "Loading..."
                    ? "Loading..."
                    : formatQuarterDisplay(quarterDropdownCurrent)}
                </span>
              </div>
            </Dropdown>
          </div>
        )}

          {/* Notifications - Always show, with badge count when messages exist */}
          {showMessages && (
            <Badge count={messageCount} className="mb-0">
              <Popover
                overlayClassName="gx-popover-horizantal"
                placement="bottomRight"
                content={
                  <AdminNotifications
                    isAdmin={isUserAdmin}
                    data={messages}
                  />
                }
                trigger="click"
              >
                <Button
                  className="mr-0 mb-0 gx-flex-row align-items-center"
                  id="report-msg-box"
                  onClick={onNotificationClick}
                >
                  <i className="icon icon-chat-new" style={{marginTop: '2px'}} />
                </Button>
              </Popover>
            </Badge>
          )}

          {showDownload && (
            <Tooltip title={<IntlMessages id="report.download" />}>
              <Button
                onClick={onDownload}
                className="mb-0"
                id="report-download"
              >
                <DownloadOutlined className="font-20" />
              </Button>
            </Tooltip>
          )}

      </div>
    </div>
  );
};

export default ReportActionButtons;
